import * as React from 'react';

import { ComboBox, formatAddress } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import {
  AddressFormState,
  HereAutosuggestItem,
  stringifyAddress,
  toAddressFormState,
  cn,
} from '@rematter/ui-web/lib';

import { useTranslations } from '~/app/i18n/useTranslations';
import { config } from '~/config';

interface HereSuggestion {
  title: string;
  id: string;
  address: { label: string };
  position?: { lat: number; lng: number };
}

async function hereLookup(id: string): Promise<HereAutosuggestItem | null> {
  const params = new URLSearchParams({
    id,
    apiKey: config.here.apiKey ?? '',
  });
  const res = await fetch(
    `https://lookup.search.hereapi.com/v1/lookup?${params}`,
  );
  if (!res.ok) return null;
  return res.json();
}

function useHereAutosuggest(defaultValue?: string) {
  const [query, setQuery] = React.useState(defaultValue ?? '');
  const [suggestions, setSuggestions] = React.useState<HereSuggestion[]>([]);
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  React.useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    timerRef.current = setTimeout(async () => {
      const params = new URLSearchParams({
        q: query,
        apiKey: config.here.apiKey ?? '',
        limit: '5',
        at: '0,0',
      });

      try {
        const res = await fetch(
          `https://autosuggest.search.hereapi.com/v1/autosuggest?${params}`,
        );
        if (!res.ok) return;
        const data = await res.json();
        const items = (data.items ?? []).filter(
          (item: HereSuggestion) => item.address && item.position,
        );
        setSuggestions(items);
      } catch {
        setSuggestions([]);
      }
    }, 300);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query]);

  const clearSuggestions = React.useCallback(() => setSuggestions([]), []);

  return { query, setQuery, suggestions, clearSuggestions };
}

interface StreetAddressFieldProps {
  value: AddressFormState;
  onChange: (value: AddressFormState) => void;
  isReadOnly?: boolean;
  className?: string;
  isRequired?: boolean;
  autoFocus?: boolean;
  showFullAddress?: boolean;
}

export const StreetAddressField = ({
  value,
  onChange,
  isReadOnly,
  className,
  isRequired,
  autoFocus,
  showFullAddress = false,
}: StreetAddressFieldProps) => {
  const toast = useToast();
  const t = useTranslations();

  const { query, setQuery, suggestions, clearSuggestions } = useHereAutosuggest(
    value ? stringifyAddress(value) : undefined,
  );

  const handleSelect = React.useCallback(
    async (suggestion?: HereSuggestion) => {
      if (!suggestion) return;

      setQuery(suggestion.address.label);
      clearSuggestions();

      try {
        const details = await hereLookup(suggestion.id);
        if (!details) return;
        const newAddress = toAddressFormState(details);
        onChange(newAddress);
      } catch (error) {
        console.error('Error selecting address:', error);
        toast.error(t.address.errors.geocodingFailed);
      }
    },
    [t, onChange, setQuery, clearSuggestions, toast],
  );

  const options = React.useMemo(
    () =>
      suggestions.map((item) => {
        const parts = item.title.split(', ');
        const mainText = parts[0] ?? item.title;
        const secondaryText = parts.slice(1).join(', ');

        return {
          id: item.id,
          label: item.title,
          mainText,
          secondaryText,
          suggestion: item,
        };
      }),
    [suggestions],
  );

  return (
    <ComboBox
      className={cn('@StreetAddressField', className)}
      label={t.address.fields.streetAddress}
      options={options}
      autoFocus={autoFocus}
      value={value.streetAddress ? [stringifyAddress(value)] : []}
      onChange={(values) => {
        const selectedValue = values?.[0];
        if (selectedValue) {
          const option = options.find((o) => o.id === selectedValue);
          if (option) {
            handleSelect(option.suggestion);
          }
        }
      }}
      query={query}
      onChangeQuery={setQuery}
      openingTriggers={['focus', 'click']}
      placeholder={t.address.placeholders.streetAddress}
      isRequired={isRequired}
      isDisabled={isReadOnly}
      renderOption={(option, state) => (
        <div
          className={cn(
            'mt-1 rounded px-3 py-2 pr-6',
            state.focused && 'bg-primary text-white',
          )}
        >
          <div className="flex flex-col">
            <span>{option.mainText}</span>
            <span className="text-sm opacity-75">{option.secondaryText}</span>
          </div>
        </div>
      )}
      getSelection={(_ids: string[]) => [
        {
          label: showFullAddress ? formatAddress(value) : value.streetAddress,
        },
      ]}
    />
  );
};
